import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./Dashboard.css";
import BarChartComponent from "./BarChartComponent";
import PieChartSummary from "./PieChartSummary";
import BookingsTable from "./BookingsTable";
import Loader from "../../components/Loader";
import { useContext } from "react";
import AuthContext from "../../contexts/AuthProvider";
import { useNavigate } from "react-router-dom";

import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const Dashboard = () => {
  const { t } = useTranslation("dash");
  const [bookings, setBookings] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useContext(AuthContext);
  const Navigate = useNavigate();
  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };
  // New filter states lifted from the child component.
  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");

  // Retrieve user from local storage and parse it
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  // Function to update filters coming from the child component.
  const handleFilterChange = (year, month) => {
    setFilterYear(year);
    setFilterMonth(month);
  };

  useEffect(() => {

    if (!isAuthenticated) {
      Navigate("/login");
      return;
    }

    // Build URL with query parameters for chart, year, and month (if provided)
    let url = new URL(`${baseUrl}/api/v1/attnd-venue/bookings`);
    const params = new URLSearchParams({ chart: "true" });
    if (filterYear) {
      params.append("year", filterYear);
    }
    if (filterMonth) {
      params.append("month", filterMonth);
    }
    url.search = params.toString();

    fetch(url.toString(), {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        lang: getLangFromLocalStorage(),

      },
    })
      .then((response) => {
        // If unauthorized, remove token and user from local storage and redirect
        if (response.status === 401) {
          localStorage.removeItem("token");
           
          Navigate("/login");
          throw new Error("Unauthorized");
        }
        if (!response.ok) {
          return response.json().then((errData) => {
            throw new Error(errData.error || "Network response was not ok");
          });
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          if (data.data) {
            setBookings(data.data);
          }
          if (data.statistics) {
            setStatistics(data.statistics);
          }
          if (data.monthlyData) {
            setMonthlyData(data.monthlyData);
          }
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching bookings:", error);
        setError(error);
        setLoading(false);
      });
    // Re-run fetch when filterYear or filterMonth change.
  }, [filterYear, filterMonth]);

  if (loading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        {t("error", { message: error.message })}
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1 className="dashboard-title">
        {t("title", {
          name: user
            ? `${user.firstName} ${user.familyName}`
            : t("defaultName"),
        })}
      </h1>

      <div className="dashboard-grid">
        {/* Statistics Card with BarChart */}
        <div className="dashboard-card dashboard-chart">
          <BarChartComponent
            statistics={statistics}
            monthlyData={monthlyData}
            onFilterChange={handleFilterChange}  // Passing down the filter change callback.
          />
        </div>

        {/* Summary Card with PieChart */}
        <div className="dashboard-card dashboard-summary">
          <div
            className="dashboard-summary"
            style={{ width: "100%", minHeight: 360 }}
          >
            <PieChartSummary statistics={statistics} />
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <BookingsTable bookings={bookings} />
    </div>
  );
};

export default Dashboard;
